import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'business.business';
  siteName?: string;
  structuredData?: object | object[];
}

const SITE_ORIGIN = 'https://www.illinoislawnhub.com';

const defaultSoftwareApplication = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'LawnHub',
  alternateName: 'Illinois LawnHub',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'Lawn Care Business Software',
  description:
    'All-in-one lawn care and snow removal business platform. Schedule jobs, manage crews and customers, track finances, and get paid — nationwide. No forced monthly subscription.',
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/logo512.png`,
  image: `${SITE_ORIGIN}/logo512.png`,
  operatingSystem: 'Web',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    description: 'No forced monthly subscription. Platform fees 1–5% when jobs are completed.',
  },
  featureList: [
    'Lawn care CRM',
    'Job scheduling',
    'Crew management',
    'Route optimization',
    'Customer payments',
    'Financial dashboard',
    'Snow removal operations',
  ],
  areaServed: {
    '@type': 'Country',
    name: 'United States',
  },
  provider: {
    '@type': 'Organization',
    name: 'LawnHub',
    url: SITE_ORIGIN,
  },
};

const defaultOrganization = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'LawnHub',
  alternateName: 'Illinois LawnHub',
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/logo512.png`,
  description:
    'Lawn care and snow removal business platform helping providers manage operations and grow nationwide.',
  areaServed: {
    '@type': 'Country',
    name: 'United States',
  },
  sameAs: [
    'https://www.facebook.com/profile.php?id=61581967089313',
    'https://www.instagram.com/illinoislawnhub/',
  ],
};

const defaultWebSite = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'LawnHub',
  alternateName: 'Illinois LawnHub',
  url: SITE_ORIGIN,
  description:
    'Lawn care business platform for scheduling, crews, customers, and payments — nationwide.',
  publisher: {
    '@type': 'Organization',
    name: 'LawnHub',
  },
};

const SEO: React.FC<SEOProps> = ({
  title = 'LawnHub — Lawn Care Business Platform',
  description =
    'All-in-one platform for lawn care and snow removal businesses. Manage scheduling, crews, customers, and payments — nationwide. No forced monthly subscription.',
  keywords =
    'lawn care business platform, lawn care CRM, lawn care scheduling software, snow removal business software, landscaping software, crew management, Jobber alternative, lawn care route optimization',
  image = '/logo512.png',
  url = typeof window !== 'undefined' ? window.location.pathname : '/',
  type = 'website',
  siteName = 'LawnHub',
  structuredData,
}) => {
  const fullTitle = title.includes('LawnHub') ? title : `${title} | LawnHub`;
  const fullUrl = url.startsWith('http') ? url : `${SITE_ORIGIN}${url.startsWith('/') ? url : `/${url}`}`;
  const fullImage = image.startsWith('http') ? image : `${SITE_ORIGIN}${image}`;

  const extraGraphs = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  const jsonLdBlocks = [defaultOrganization, defaultWebSite, defaultSoftwareApplication, ...extraGraphs];

  return (
    <Helmet>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="LawnHub" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:alt" content="LawnHub lawn care business software" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:image:alt" content="LawnHub lawn care business software" />

      {jsonLdBlocks.map((block, i) => (
        <script key={`ld-${i}-${(block as any)['@type'] || i}`} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
